import {
    commaSeparate, getArrayMaxByProperty,
    getChunks,
    getCurrentGame, getRandomChoice,
    getRandomSample,
    getRosterMemberPopularity,
    objectInArray,
    range
} from "./helpers.js";
import {getGameCurrentUserCompany} from "./games.js";
import {
    getAvailableStaffMembersByRole,
    getCompanyLatestEvent,
    getCompanyParentEvents,
    getCompanyRosterByType
} from "./companies.js";
import {addRosterTagTeams, getRosterSuperstars, getRosterTagTeams} from "../models/roster.js";
import {db, TagTeams, Championships, Matches, Events, MatchContracts, Tournaments} from "../models/models.js";
import {
    addEventMatchContracts,
    addEventMatches,
    addEventTournaments,
    getEventTournaments
} from "../models/event.js";
import {
    addCompanyChampionships,
    addCompanyEvents,
    addCompanyTournaments,
    getCompanyChampionships, getCompanyStaff,
    getCompanyTournaments, removeCompanyEvents
} from "../models/company.js";
import {
    checkAvailability,
    checkSuperstarBrandAvailability,
    getRosterMemberSuperstars,
    getSuperstarChampionshipsFromCompanyChampionships, getSuperstarLastName
} from "./superstars.js";
import {addTagTeamSuperstars, TagTeam} from "../models/tagTeam.js";
import {Event} from "../models/event.js";
import {
    addTournamentChampionships,
    addTournamentMatchContracts,
    addTournamentMatches, getTournamentMatches,
    Tournament
} from "../models/tournament.js";
import {
    getEventMatchesData,
    getEventSegmentsData
} from "./events.js";
import {
    checkAreChampionshipsEqual
} from "./championships.js";
import {
    addMatchChampionships,
    addMatchParticipants, getMatchParticipants,
    Match
} from "../models/match.js";
import {MatchContract} from "../models/matchContract.js";
import {addChampionshipMatchContracts, Championship} from "../models/championship.js";
import {setManagersForMatchOrSegment} from "./managers.js";
import {globalUser} from "./globals.js";
import {addFeudsToMatch, getHypeForMatch} from "./matches.js";


async function getTournamentDisplay({participantsData, tournamentType, matchType, numberOfGroups} = {}) {  // get_tournament_display_view
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterTagTeams = await getRosterTagTeams(mainRoster);

    // Get participants
    let participants = [];
    for (const x of participantsData) {
        let participant;
        if (matchType === 'singles') {
            participant = rosterSuperstars.find(y => y.$loki === x.uid)
        }
        else if (matchType === 'tag') {
            participant = rosterTagTeams.find(y => y.$loki === x.uid)
        }
        // Check participant
        if (!participant) {
            data['error'] = 'participant-not-found';
            return data;
        }
        if (objectInArray(participant, participants)) {
            data['error'] = 'duplicate-participant';
            return data;
        }
        participants.push(participant)
    }

    // Get bracket/table
    let participantSeeds = await createTournamentParticipantSeeds([...participants]);
    if (tournamentType === 'elimination') {
        data['bracket'] = getTournamentBracketFromSeeds(participantSeeds);
    }
    else if (tournamentType === 'round-robin') {
        data['table'] = getTournamentGroupsFromSeeds(participantSeeds, numberOfGroups);
    }

    db.saveDatabase();

    return data;
}

async function createTournamentParticipantSeeds(participants) {  // create_tournament_seeds
    // Create seeds from 1 to len(participants) based on popularity, returns [participant, seed]
    let participantSeeds = [];

    // Sort by popularity
    for (const x of participants) {
        x.tempPopularity = await getRosterMemberPopularity(x);
    }
    participants.sort((a, b) => b.tempPopularity - a.tempPopularity);
    for (const x of participants) {
        delete x.tempPopularity;
    }

    // Add seeds, set tournamentSeed for participants
    for (const [i, p] of participants.entries()) {
        let seed = i + 1;
        participantSeeds.push([p, seed]);
        p.tournamentSeed = seed;
    }

    return participantSeeds;
}

function getTournamentBracketFromSeeds(participantSeeds) {  // get_tournament_bracket_from_seeds
    // Start with finals node, go backwards to create bracket

    let nextNodes = [participantSeeds[0], participantSeeds[1]];
    let numberOfRounds = Math.log2(participantSeeds.length);

    for (const z of range(numberOfRounds - 1)) {
        let nodes = [...nextNodes];
        nextNodes = [];

        // Add seed to previous round
        for (const x of nodes) {
            nextNodes.push(...[x, null])
        }

        let nextSeeds = participantSeeds.slice(nextNodes.length / 2, nextNodes.length);
        for (const x of nextSeeds) {
            // Find seed to pair with
            let i = nextNodes.length + 1 - x[1];
            // Add seed to node after i
            let node = nextNodes.find(y => y && y[1] === i);
            let pos = nextNodes.indexOf(node) + 1;
            nextNodes[pos] = x
        }
    }

    // Reverse second half
    nextNodes = nextNodes.splice(0,nextNodes.length / 2).concat(nextNodes.reverse());

    // Initialize brackets with numberOfRounds
    let bracket = {};
    for (const x of range(numberOfRounds, 1)) {
        bracket[x] = []
    }

    // Divide nodes into two superstars
    let matches = getChunks(nextNodes, 2);
    for (const x of matches) {
        let superstars = [x[0][0], x[1][0]];
        // Get match bracket
        let data = {
            'participants': superstars.map(s => s ? [s.name, s.tournamentSeed] : [null, null])
        };
        bracket[1].push(data);
    }

    // Add empty future numberOfRounds' matches
    for (const x of range(numberOfRounds, 1)) {
        if (!bracket[x].length) {
            let n = Math.floor(matches.length * 2 / (2 ** (x)));  // Number of matches for this round
            for (const m of range(n)) {
                let data = {
                    'participants': [[null, null], [null, null]]
                };
                bracket[x].push(data);
            }
        }
    }

    return bracket;
}

function getTournamentGroupsFromSeeds(participantSeeds, numberOfGroups) {  // get_tournament_groups_from_seeds
    // Create empty groups
    let groups = Array.from({ length: numberOfGroups }).map(() => []);

    // Divide participants into chunks of numberOfGroups
    let seedsChunks = getChunks(participantSeeds, numberOfGroups);

    // Go through each chunk, adding participant name and seed to their group
    for (const seedChunk of seedsChunks) {
        for (const [i, participantSeed] of seedChunk.entries()) {
            groups[i].push([participantSeed[0].name, participantSeed[1]])
        }
    }

    return groups;
}

async function createTournament({participantsData, name, tournamentName, type, matchesAtEvent, matchType,
                                 matchContractName, matchContractEventName, awardOption, participantsAdvancing,
                                 triosMatch, selectedChampionshipUid} = {}) {  // create_tournament
    let data = {};
    const currentGame = await getCurrentGame();
    const userCompany = await getGameCurrentUserCompany(currentGame);
    const mainRoster = await getCompanyRosterByType(userCompany, 'main');
    const rosterSuperstars = await getRosterSuperstars(mainRoster);
    const rosterTagTeams = await getRosterTagTeams(mainRoster);
    const latestEvent = await getCompanyLatestEvent(userCompany);
    const companyTournaments = await getCompanyTournaments(userCompany);
    const companyChampionships = await getCompanyChampionships(userCompany);
    const companyStaffMembers = await getCompanyStaff(userCompany);
    const cost = 150;

    // Check if enough points
    if (userCompany.points < cost) {
        data['error'] = 'not-enough-points';
        return data;
    }

    // Check if tournament already booked
    if ((await getEventTournaments(latestEvent)).length > 0) {
        data['error'] = 'tournament-booked';
        return data;
    }

    // Check name uniqueness if new tournament
    if (!tournamentName) {
        const tournamentNames = companyTournaments.map(x => x.name);
        if (tournamentNames.includes(name)) {
            data['error'] = 'non-unique-name';
            return data;
        }
    }
    // Otherwise set name to previous tournament's name
    else {
        name = tournamentName
    }

    // Check if match contract exists
    if (matchContractName) {
        if (companyChampionships.find(x => x.name === matchContractName)) {
            data['error'] = 'match-contract-exists';
            return data;
        }
    }

    // Get participants
    let participants = [];
    let participantsDataFormatted = [...participantsData];
    if (type === 'round-robin') {
        participantsDataFormatted = participantsData.flat();
    }
    for (const x of participantsDataFormatted) {
        let participant;
        if (matchType === 'singles') {
            participant = rosterSuperstars.find(y => y.name === x)
        }
        else if (matchType === 'tag') {
            participant = rosterTagTeams.find(y => y.name === x);
            // If trio selected in tag match, create temporary tag team of 2 of trio's superstars
            if (participant && participant.trio && !triosMatch) {
                let teamSuperstars = await getRosterMemberSuperstars(participant);
                let availableTeamSuperstars = teamSuperstars.filter(y => checkAvailability(y, false)
                    && checkSuperstarBrandAvailability(latestEvent, y,
                        getSuperstarChampionshipsFromCompanyChampionships(y, companyChampionships)));
                // Check if enough team superstars available
                if (availableTeamSuperstars.length < 2) {
                    data['error'] = 'not-available';
                    return data;
                }

                // Create temporary tag team
                let temporaryTeamSuperstars = getRandomSample(availableTeamSuperstars, 2);
                let temporaryTeamSuperstarsNames = commaSeparate([...temporaryTeamSuperstars]
                    .sort((a, b) => a.$loki - b.$loki).map(y => getSuperstarLastName(y)));
                let teamName = `${participant.name} (${temporaryTeamSuperstarsNames})`;
                const newTagTeam = TagTeam(teamName, true);
                newTagTeam.trioTagTeam = true;
                await TagTeams.insert(newTagTeam);
                await addTagTeamSuperstars(newTagTeam, temporaryTeamSuperstars);
                await addRosterTagTeams(mainRoster, [newTagTeam]);
                participant = newTagTeam;
            }
        }
        if (!participant) {
            data['error'] = 'not-found';
            return data;
        }
        participants.push(participant);
    }

    // Get participant superstars
    let participantSuperstars = (await Promise.all(participants.map(x => getRosterMemberSuperstars(x)))).flat();

    // Check if any superstars are present twice
    for (const s of participantSuperstars) {
        if (participantSuperstars.filter(x => x.$loki === s.$loki).length > 1) {
            data['error'] = 'duplicate-superstar';
            data['duplicateSuperstar'] = s.name;
            return data;
        }
    }

    // Check for booking restrictions on event
    let eventActiveEffect = latestEvent.activeEffect;
    if (['no_women_bookable', 'no_men_bookable'].includes(eventActiveEffect)) {
        for (const s of participantSuperstars) {
            if (eventActiveEffect === 'no_women_bookable' && s.gender === 'female') {
                data['error'] = 'no-women-allowed';
                return data;
            }
            else if (eventActiveEffect === 'no_men_bookable' && s.gender === 'male') {
                data['error'] = 'no-men-allowed';
                return data;
            }
        }
    }

    // Create first round matches
    let firstRoundMatches;
    let rounds;
    let groupSize;
    let numberOfGroups;
    let knockoutRounds;
    if (type === 'elimination') {
        rounds = Math.log2(participants.length);
        firstRoundMatches = getChunks([...participants].reverse(), 2);
    }
    else if (type === 'round-robin') {
        numberOfGroups = participantsData.length;
        let groupRounds = participantsData[0].length - 1;
        if (participantsAdvancing > 0) {
            knockoutRounds = Math.log2(participantsAdvancing * numberOfGroups);
        }
        else {
            knockoutRounds = 0
        }
        rounds = groupRounds + knockoutRounds;
        groupSize = participants.length / numberOfGroups;
        let groups = getChunks(participants, groupSize);
        firstRoundMatches = (await createGroupMatches(groups)).reverse();
        for (const x of participants) {
            x.tournamentPoints = 0;
        }
    }

    // Get matches to hold at next event
    let matchesAtEventNumber;
    if (participantsAdvancing !== 0) {
        let matchesAtEventValues = {'finals': 1, 'semi-finals': 2, 'quarter-finals': 4};
        matchesAtEventNumber = matchesAtEventValues[matchesAtEvent] || 4;
    }
    else {
        // No knockout round
        matchesAtEventNumber = parseInt(matchesAtEvent);
    }

    // Set matches at event to first round matches if smaller than it
    if (firstRoundMatches.length < matchesAtEventNumber) {
        matchesAtEventNumber = firstRoundMatches.length;
    }

    let tournamentEvent;
    let weeklyShowCreated = false;
    let addedMatches = [];
    if (firstRoundMatches.length > 4 || firstRoundMatches.length > matchesAtEventNumber) {
        // Create weekly show
        let weeklyShow = Event('Weekly Show');
        weeklyShow.monthlyEventName = latestEvent.name;
        weeklyShow.brandName = latestEvent.brandName;
        await Events.insert(weeklyShow);
        await addCompanyEvents(userCompany, [weeklyShow]);
        tournamentEvent = weeklyShow;
        weeklyShowCreated = true;

        if (type === 'elimination') {
            // Calculate rounds to add at event, add future matches
            let roundsData = {'finals': rounds - 1, 'semi-finals': rounds - 2, 'quarter-finals': rounds - 3};
            let matchesAtEventRounds = roundsData[matchesAtEvent] || rounds - 3;
            let futureMatches = await createFutureEliminationMatches(matchesAtEventRounds, rounds, firstRoundMatches,
                latestEvent, matchType, weeklyShow);
            addedMatches.push(...futureMatches);

            // Add matches to weekly show
            let addedEventMatches = await addMatchesToEvent(firstRoundMatches, weeklyShow, matchType, rosterSuperstars);
            addedMatches.push(...addedEventMatches);
        }
        else if (type === 'round-robin') {
            if (participantsAdvancing === 0) {
                // Add matches to weekly show and next event if no knockout stage
                let matchesIndex;
                if (matchesAtEventNumber === 0) {
                    matchesIndex = Math.floor(groupSize / 2)  // Final round matches
                }
                else {
                    matchesIndex = matchesAtEventNumber  // Selected matches
                }
                let weeklyShowMatches = firstRoundMatches.slice(matchesIndex);
                let eventMatches = firstRoundMatches.slice(0, matchesIndex);
                let addedWeeklyShowMatches = await addMatchesToEvent(weeklyShowMatches, weeklyShow, matchType,
                    rosterSuperstars, true);
                let addedEventMatches = await addMatchesToEvent(eventMatches, latestEvent, matchType, rosterSuperstars,
                    true);
                addedMatches.push(...addedWeeklyShowMatches);
                addedMatches.push(...addedEventMatches);

                if (!addedWeeklyShowMatches.length) {
                    await removeCompanyEvents(userCompany, [weeklyShow]);
                    Events.remove(weeklyShow);
                    weeklyShowCreated = false;
                    tournamentEvent = latestEvent;
                }
            }
            else {
                // Add knockout matches to weekly show and next event
                let roundsData = {'finals': 1, 'semi-finals': 2, 'quarter-finals': 3};  // Rounds to add
                let eventRounds = roundsData[matchesAtEvent] || 3;
                let weeklyRounds = knockoutRounds - eventRounds;
                roundsData = {'finals': rounds, 'semi-finals': rounds - 1,
                               'quarter-finals': rounds - 2};  // Match rounds
                let eventRound = roundsData[matchesAtEvent] || 2;
                let weeklyRound = eventRound - weeklyRounds;
                let futureGroupMatchesEvent = await createFutureGroupMatches(eventRounds, latestEvent, rounds, eventRound,
                    matchType);
                let futureGroupMatchesWeeklyShow = await createFutureGroupMatches(weeklyRounds, weeklyShow, rounds,
                    weeklyRound, matchType);
                addedMatches.push(...futureGroupMatchesEvent);
                addedMatches.push(...futureGroupMatchesWeeklyShow);
                // Add group stage matches to weekly show
                let addedEventMatches = await addMatchesToEvent(firstRoundMatches, weeklyShow, matchType,
                    rosterSuperstars, true);
                addedMatches.push(...addedEventMatches);
            }
        }
    }
    else {
        // Add matches to event
        if (type === 'elimination') {
            let futureMatches = await createFutureEliminationMatches(1, rounds, firstRoundMatches,
                latestEvent, matchType);
            addedMatches.push(...futureMatches);
            let addedEventMatches = await addMatchesToEvent(firstRoundMatches, latestEvent, matchType, rosterSuperstars);
            addedMatches.push(...addedEventMatches);
        }
        tournamentEvent = latestEvent;
    }

    // Find previous tournament with max prestige
    let previousTournament;
    if (tournamentName) {
        previousTournament = getArrayMaxByProperty(companyTournaments.filter(x => x.name === tournamentName), 'xp');
    }

    // Create tournament
    let tournament = Tournament(name, type, rounds, matchesAtEventNumber, participantsAdvancing, knockoutRounds,
        numberOfGroups);
    await Tournaments.insert(tournament);

    // Add previous tournament's prestige data if found
    if (previousTournament) {
        tournament.prestige = previousTournament.prestige;
        tournament.xp = previousTournament.xp;
        tournament.startingXp = previousTournament.startingXp;
        tournament.cost = previousTournament.cost;
    }

    // Add tournament relationships
    await addTournamentMatches(tournament, addedMatches);
    await addEventTournaments(tournamentEvent, [tournament]);
    await addCompanyTournaments(userCompany, [tournament]);

    // Remove cost
    userCompany.points -= tournament.cost;

    // Add championship award
    if (awardOption === 'championship') {
        let championship = await Championships.get(selectedChampionshipUid);
        if (championship) {
            await addTournamentChampionships(tournament, [championship]);
            for (const x of companyChampionships.filter(y => checkAreChampionshipsEqual(y, championship))) {
                x.booked = true;
            }
            // Add to final match
            let finalMatch = getArrayMaxByProperty(addedMatches, 'tournamentRound');
            await addMatchChampionships(finalMatch, [championship]);
            // Add to tournament awardName
            tournament.awardName = championship.name + ' Championship';
        }
    }
    // Add match contract award
    else if (awardOption === 'match-contract') {
        let parentEvents = await getCompanyParentEvents(userCompany);
        let parentEvent = parentEvents.find(x => x.name.trim() === matchContractEventName);

        let matchContract = MatchContract();
        matchContract.name = matchContractName;
        matchContract.awarded = false;
        let bonuses = {'C': 12, 'B': 25, 'A': 50};
        matchContract.bonus = bonuses[parentEvent.prestige] || 12;
        await MatchContracts.insert(matchContract);
        await addTournamentMatchContracts(tournament, [matchContract]);
        tournament.matchContractName = matchContractName;
        tournament.awardName = matchContractName + ' Contract';

        // Add match contract to parent event
        await addEventMatchContracts(parentEvent, [matchContract]);

        // Get number of championships to create
        let numberOfChampionships = 1;
        if (matchType === 'tag') {
            if (triosMatch) {
                numberOfChampionships = 3
            }
            else {
                numberOfChampionships = 2
            }
        }

        // Create championships
        let createdChampionships = [];
        for (const x of Array(numberOfChampionships).keys()) {
            const newChampionship = Championship(matchContractName);
            newChampionship.matchContract = true;
            newChampionship.booked = true;
            newChampionship.vacated = true;
            newChampionship.rank = 'Upper Midcarder';
            if (numberOfChampionships === 2) {
                newChampionship.tagTeam = true;
            }
            else if (numberOfChampionships === 3) {
                newChampionship.trio = true;
            }
            await Championships.insert(newChampionship);
            await addChampionshipMatchContracts(newChampionship, [matchContract]);
            createdChampionships.push(newChampionship);
        }

        // Add to company
        await addCompanyChampionships(userCompany, createdChampionships);

        // Add to final match
        let finalMatch = getArrayMaxByProperty(addedMatches, 'tournamentRound');
        await addMatchChampionships(finalMatch, [createdChampionships[createdChampionships.length - 1]]);
    }

    db.saveDatabase();

    // Get data to return
    data['points'] = userCompany.points;
    const producers = getAvailableStaffMembersByRole(companyStaffMembers, 'producer');
    const matches = await getEventMatchesData({event: latestEvent, rosterSuperstars: rosterSuperstars,
        getChampionshipsAvailable: false});
    const segments = await getEventSegmentsData({event: latestEvent, rosterSuperstars: rosterSuperstars,
        producers: producers});
    data['matchesAndSegments'] = matches.concat(segments);
    data['matchesAndSegments'].sort((a, b) => a.order - b.order);
    data['weeklyShowCreated'] = weeklyShowCreated;

    return data;
}

async function createFutureEliminationMatches(eventRoundStart, rounds, firstRoundMatches, latestEvent, matchType,
                                              weeklyEvent = null) {
    let addedMatches = [];

    // Add matches to next event
    for (let z = rounds + 1; z > eventRoundStart; z--) {
        let matchesToAdd = Math.floor(firstRoundMatches.length * 2 / (2 ** z));
        for (const y of range(matchesToAdd)) {
            const newMatch = await createFutureTournamentMatch(latestEvent, z, matchType);
            addedMatches.push(newMatch);
        }
    }

    // If tournament starting at weekly show, add future matches to it
    if (weeklyEvent) {
        for (let z = eventRoundStart; z > 1; z--) {
            let matchesToAdd = Math.floor(firstRoundMatches.length * 2 / (2 ** z));
            for (const y of range(matchesToAdd)) {
                const newMatch = await createFutureTournamentMatch(weeklyEvent, z, matchType);
                addedMatches.push(newMatch);
            }
        }
    }

    return addedMatches;
}

async function createFutureTournamentMatch(event, tournamentRound, matchType) {
    const newMatch = Match(event.matchesCount, matchType);
    newMatch.tournamentRound = tournamentRound;
    await Matches.insert(newMatch);
    await addEventMatches(event, [newMatch]);
    event.matchesCount += 1;
    return newMatch;
}

async function createFutureGroupMatches(rounds, event, tournamentRounds, matchRound, matchType)  {  // create_future_group_matches
    // Add knockout rounds
    let addedMatches = [];
    for (let z = rounds - 1; z > -1; z--) {
        let currentRound = matchRound + z;
        let matchesToAdd = 2 ** (tournamentRounds - currentRound);
        for (const y of range(matchesToAdd)) {
            const newMatch = await createFutureTournamentMatch(event, currentRound, matchType);
            addedMatches.push(newMatch);
        }
    }
    return addedMatches;
}

async function createGroupMatches(groups) {
    let matches = [];

    for (const [i, group] of groups.entries()) {
        let groupMembers = [...group];
        let firstMember = groupMembers[0];

        // Circle algorithm
        let rows = getChunks(groupMembers, Math.floor(groupMembers.length / 2));
        let row1 = rows[0];
        let row2 = rows[1].reverse();  // Divide into two halves, reverse second half

        // Create round's matches
        for (const matchRound of range(groupMembers.length - 1)) {
            for (const x of range(Math.floor(groupMembers.length / 2)).reverse()) {  // Top seed matches at the end
                // Get corresponding value in row
                let groupValue = 0;
                if (groups.length > 1) {
                    groupValue = i + 1
                }
                let match = [[row1[x], row2[x]], groupValue, matchRound + 1];  // Superstars, group, round
                matches.push(match);
            }
            // Rotate all elements except first member
            let l = groupMembers.filter(x => x.$loki !== firstMember.$loki);
            let rotated = l.slice(l.length - 1).concat(l.slice(0, l.length - 1));
            groupMembers = [firstMember].concat(rotated);

            rows = getChunks(groupMembers, Math.floor(groupMembers.length / 2));
            row1 = rows[0];
            row2 = rows[1].reverse();
        }
    }

    matches.sort((a, b) => a[2] - b[2]);

    return matches;
}

async function addMatchesToEvent(matchesData, event, matchType, rosterSuperstars, group = false) {
    let addedMatches = [];
    for (const matchData of matchesData) {

        // Add match
        const newMatch = Match(event.matchesCount, matchType);
        if (group) {
            newMatch.tournamentGroup = matchData[1];
            newMatch.tournamentRound = matchData[2];
        }
        else {
            newMatch.tournamentRound = 1;
        }
        await Matches.insert(newMatch);
        await addEventMatches(event, [newMatch]);
        event.matchesCount += 1;
        addedMatches.push(newMatch);

        // Add participants
        let matchParticipants = group ? matchData[0] : matchData;
        await addMatchParticipants(newMatch, matchParticipants);

        // Get match superstars
        let matchSuperstars = (await Promise.all(matchParticipants.map(x => getRosterMemberSuperstars(x)))).flat();

        // Reset attires
        for (const s of matchSuperstars) {
            if (s.currentAttire > 0) {
                s.currentAttire = 0;
            }
        }

        // Add feuds
        await addFeudsToMatch(newMatch, matchParticipants);

        // Add managers
        if (globalUser.paid) {
            setManagersForMatchOrSegment(newMatch, matchSuperstars, rosterSuperstars, event)
        }
    }
    return addedMatches;
}

async function getAdvancingParticipants(tournament, group, numberToAdvance = 1) {  // get_advancing_participants
    // Get group's matches
    let groupMatches = (await getTournamentMatches(tournament)).filter(x => x.tournamentGroup === group);

    // Add participants using first round matches
    let participants = [];
    const firstRoundMatches = groupMatches.filter(x => x.tournamentRound === 1);
    for (const m of firstRoundMatches) {
        const matchParticipants = await getMatchParticipants(m);
        for (const p of matchParticipants) {
            participants.push(p);
            p.tempPoints = 0;
        }
    }

    let advancing;
    let advancingPoints;
    if (numberToAdvance === 1) {
        // Get winner by maximum points
        advancing = [getArrayMaxByProperty(participants, 'tournamentPoints')];
        advancingPoints = advancing[0].tournamentPoints;
    } else {
        // Sort by points
        const sortedParticipants = participants.sort((a, b) => b.tournamentPoints - a.tournamentPoints);
        advancing = sortedParticipants.slice(0, numberToAdvance);
        advancingPoints = advancing[numberToAdvance - 1].tournamentPoints;
    }

    // Check if tie at advancing points
    const tiedParticipants = participants.filter(x => x.tournamentPoints === advancingPoints);
    if (tiedParticipants.length > 1) {
        // Create sub table of max participants
        for (const p of tiedParticipants) {
            const opponents = tiedParticipants.filter(x => x.$loki !== p.$loki);
            for (const m of groupMatches) {
                const matchParticipants = await getMatchParticipants(m);
                // Check if participant and an opponent in match participants
                if (objectInArray(p, matchParticipants) && opponents.some(x => objectInArray(x, matchParticipants))) {
                    if (['Double Pin', 'Time-limit Draw'].includes(m.finishName)) {
                        p.tempPoints += 1;
                    } else if (m.winner === p.name) {
                        p.tempPoints += 2;
                    }
                }
            }
        }

        if (numberToAdvance === 1) {
            advancing = [getArrayMaxByProperty(tiedParticipants, 'tempPoints')];
        } else {
            // Secondary sort by temp points
            const sortedParticipants = participants.sort((a, b) => b.tournamentPoints - a.tournamentPoints ||
                b.tempPoints - a.tempPoints);
            advancing = sortedParticipants.slice(0, numberToAdvance);
        }
    }

    return advancing;
}

async function getNextRoundMatches(tournament, tournamentRound) {  // get_next_round_matches
    // Find all matches of next round
    let matches = (await getTournamentMatches(tournament)).filter(x => x.tournamentRound === tournamentRound + 1);
    // Reverse, as they are added in reverse order
    matches.reverse();
    return matches;
}

async function addWinnerToNextRound(matches, winner, event, pickRandom = false, rosterSuperstars = null,
                                    annualTournament = null) {
    // Get first incomplete match and add winner to it
    let availableMatches = [];
    for (const x of matches) {
        if ((await getMatchParticipants(x)).length < 2) {
            availableMatches.push(x)
        }
    }
    let matchToAddTo;
    if (pickRandom) {
        matchToAddTo = getRandomChoice(availableMatches)
    } else {
        matchToAddTo = availableMatches.find(x => x);
    }

    await addMatchParticipants(matchToAddTo, [winner]);

    // Add feuds and managers if full participants for match (if not annual tournament)
    const matchParticipants = await getMatchParticipants(matchToAddTo);
    if (matchParticipants.length === 2) {
        if (!annualTournament) {
            await addFeudsToMatch(matchToAddTo, matchParticipants);
            const matchSuperstars = (await Promise.all(matchParticipants.map(x => getRosterMemberSuperstars(x)))).flat();
            if (globalUser.paid) {
                setManagersForMatchOrSegment(matchToAddTo, matchSuperstars, rosterSuperstars, event);
            }
        } else {
            // Add hype for next round matches of annual tournament
            matchToAddTo.hype = await getHypeForMatch(matchParticipants, [], [], 'pure',
                annualTournament, matchToAddTo.tournamentRound);
        }
    }

    return matchToAddTo;
}

export {getTournamentDisplay, createTournament, getAdvancingParticipants, getNextRoundMatches,
    addWinnerToNextRound}